const MIXPANEL_IMPLEMENTATION_ERROR_PREFIX = 'Mixpanel error: Implementation error. Please turn on debug'
const NEXTJS_CLIENT_EXCEPTION_MESSAGE = 'A client-side exception has occurred, see here for more info'
// All three are the Next.js router abandoning a client-side transition, not an
// application fault. "Full document navigation required" is thrown with
// `cancelled: true` when the target cannot be served by the client router and
// the browser has to do a real document load; Next re-emits it through
// `routeChangeError`, so it surfaces as an unhandled rejection (NONRESIDENT-D1).
const NEXTJS_NAVIGATION_NOISE_PATTERNS = [
  'cancel rendering route',
  'hard navigate',
  'full document navigation required',
]

type SentryEventLike = {
  logger?: unknown
  request?: { url?: unknown }
  message?: unknown
  tags?: Record<string, unknown>
  logentry?: {
    formatted?: unknown
    message?: unknown
  }
  exception?: {
    values?: Array<{
      value?: unknown
      stacktrace?: {
        frames?: Array<{
          filename?: unknown
          abs_path?: unknown
        }>
      }
    }>
  }
  breadcrumbs?: Array<{
    category?: unknown
    type?: unknown
  }>
}

function getStringCandidates(event: SentryEventLike): string[] {
  const candidates = [
    event.message,
    event.logentry?.formatted,
    event.logentry?.message,
    event.exception?.values?.[0]?.value,
  ]

  return candidates.filter((candidate): candidate is string => typeof candidate === 'string')
}

function hasKnownMixpanelImplementationErrorMessage(event: SentryEventLike): boolean {
  return getStringCandidates(event).some((candidate) =>
    candidate.trim().startsWith(MIXPANEL_IMPLEMENTATION_ERROR_PREFIX),
  )
}

function hasConsoleOrigin(event: SentryEventLike): boolean {
  const loggerTag = event.tags?.logger

  if (event.logger === 'console' || loggerTag === 'console') {
    return true
  }

  return (
    event.breadcrumbs?.some((breadcrumb) => breadcrumb.category === 'console' || breadcrumb.type === 'console') ?? false
  )
}

export function shouldDropKnownMixpanelImplementationError(event: SentryEventLike): boolean {
  return hasKnownMixpanelImplementationErrorMessage(event) && hasConsoleOrigin(event)
}

function hasNextJsClientExceptionMessage(event: SentryEventLike): boolean {
  return getStringCandidates(event).some((candidate) => candidate.includes(NEXTJS_CLIENT_EXCEPTION_MESSAGE))
}

export function shouldDropNextJsClientExceptionWrapper(event: SentryEventLike): boolean {
  return hasNextJsClientExceptionMessage(event) && hasConsoleOrigin(event)
}

function hasRouteCancellationMessage(event: SentryEventLike): boolean {
  return getStringCandidates(event).some((candidate) => {
    const lower = candidate.toLowerCase()
    return NEXTJS_NAVIGATION_NOISE_PATTERNS.some((pattern) => lower.includes(pattern))
  })
}

export function shouldDropNextJsRouteCancellationError(event: SentryEventLike): boolean {
  // Drop regardless of origin — "Cancel rendering route" is always a benign
  // Next.js internal error (happens during rapid navigations / shallow routing).
  // Previous filter required hasConsoleOrigin which missed onunhandledrejection events.
  return hasRouteCancellationMessage(event)
}

function hasKnownDomMutationNoise(event: SentryEventLike): boolean {
  return getStringCandidates(event).some((candidate) => {
    const normalized = candidate.toLowerCase()
    const hasNotChildMessage = normalized.includes('not a child of this node')

    if (!hasNotChildMessage) {
      return false
    }

    return normalized.includes('removechild') || normalized.includes('insertbefore')
  })
}

export function shouldDropKnownDomMutationNoise(event: SentryEventLike): boolean {
  return hasKnownDomMutationNoise(event) && hasConsoleOrigin(event)
}

// Same failure, two browsers: Safari says "Load failed", Chrome/Firefox say
// "Failed to fetch". Matching only the first left the Chrome variant to open a
// separate issue for the same third-party outage (NONRESIDENT-6X). The host is
// the GTM/Stape proxy — nothing in the product changes based on whether it
// loads, so a network blip against it is not actionable either way.
const GTM_STAPE_HOST = 't.nonresident.tax'
const GTM_STAPE_LOAD_FAILURES = [`Load failed (${GTM_STAPE_HOST})`, `Failed to fetch (${GTM_STAPE_HOST})`]
const META_PIXEL_PREFIX = '[Meta Pixel]'
const TRANSIENT_NETWORK_ERROR_MESSAGES = ['Load failed', 'Failed to fetch']

function hasGtmStapeLoadFailure(event: SentryEventLike): boolean {
  return getStringCandidates(event).some((candidate) =>
    GTM_STAPE_LOAD_FAILURES.some((failure) => candidate.includes(failure)),
  )
}

export function shouldDropGtmStapeNetworkError(event: SentryEventLike): boolean {
  return hasGtmStapeLoadFailure(event)
}

function hasMetaPixelWarning(event: SentryEventLike): boolean {
  return getStringCandidates(event).some((candidate) => candidate.includes(META_PIXEL_PREFIX))
}

export function shouldDropMetaPixelWarning(event: SentryEventLike): boolean {
  return hasMetaPixelWarning(event)
}

function isTransientFetchNetworkError(event: SentryEventLike): boolean {
  return getStringCandidates(event).some((candidate) =>
    TRANSIENT_NETWORK_ERROR_MESSAGES.some((msg) => candidate.includes(msg)),
  )
}

export function shouldDropTransientFetchNetworkError(event: SentryEventLike): boolean {
  return isTransientFetchNetworkError(event) && hasConsoleOrigin(event)
}

// DocuSeal embeds a Vue-based form widget from cdn.docuseal.com/js/form.js.
// Errors from this third-party code are not actionable (NONRESIDENT-97, NONRESIDENT-98).
const DOCUSEAL_PATTERNS = ['data-v-app', 'initTypedSignature']
const DOCUSEAL_FRAME_PATTERNS = ['docuseal.com', '/js/form.js']

function hasDocuSealErrorPattern(event: SentryEventLike): boolean {
  return getStringCandidates(event).some((candidate) =>
    DOCUSEAL_PATTERNS.some((pattern) => candidate.includes(pattern)),
  )
}

function hasDocuSealStackFrames(event: SentryEventLike): boolean {
  const values = event.exception?.values || []
  return values.some((v) =>
    v.stacktrace?.frames?.some((frame) => {
      const filename = typeof frame.filename === 'string' ? frame.filename : ''
      const absPath = typeof frame.abs_path === 'string' ? frame.abs_path : ''
      return DOCUSEAL_FRAME_PATTERNS.some((pattern) => filename.includes(pattern) || absPath.includes(pattern))
    }),
  )
}

export function shouldDropDocuSealThirdPartyError(event: SentryEventLike): boolean {
  return hasDocuSealErrorPattern(event) || hasDocuSealStackFrames(event)
}

// Safari throws NotReadableError for native I/O failures with no app-level frames.
// These are browser-internal and not actionable (NONRESIDENT-4V).
function hasNotReadableError(event: SentryEventLike): boolean {
  return getStringCandidates(event).some((candidate) => candidate.includes('NotReadableError'))
}

export function shouldDropSafariIOReadError(event: SentryEventLike): boolean {
  return hasNotReadableError(event)
}

// Browser-extension content scripts (Grammarly, password managers, ad blockers, etc.)
// walk the DOM and try to read CSSStyleSheet.cssRules. When a parent stylesheet was
// loaded from a different origin, the browser blocks the access with a SecurityError.
// The extension catches it and console.warns the message, which captureConsoleIntegration
// forwards to Sentry. Not actionable in first-party code (NONRESIDENT-AF).
const CROSS_ORIGIN_STYLESHEET_PATTERNS = [
  'Not allowed to access cross-origin stylesheet',
  "Cannot get CSS styles from text's parentNode",
]

function hasCrossOriginStylesheetWarning(event: SentryEventLike): boolean {
  return getStringCandidates(event).some((candidate) =>
    CROSS_ORIGIN_STYLESHEET_PATTERNS.some((pattern) => candidate.includes(pattern)),
  )
}

export function shouldDropCrossOriginStylesheetWarning(event: SentryEventLike): boolean {
  return hasCrossOriginStylesheetWarning(event)
}

// MetaMask browser extension injects inpage.js into all pages. When it fails to
// connect, the error is captured by Sentry but is not actionable (NONRESIDENT-AM).
// Drop only when the error is purely from MetaMask — if first-party frames exist
// in the stack, the error may involve our code and should be kept.
const METAMASK_ERROR_PATTERNS = ['Failed to connect to MetaMask', 'MetaMask extension not found']
// Known browser-extension frame patterns. Any frame NOT matching these is assumed
// first-party (covers source-mapped src/ paths, production bundle URLs like
// /_next/static/chunks/, and any other app code format).
const KNOWN_EXTENSION_FRAME_PATTERNS = [
  'inpage.js',
  'chrome-extension://',
  'moz-extension://',
  'safari-web-extension://',
  'extension://',
  'content-script',
  'content_script',
]

function hasMetaMaskErrorMessage(event: SentryEventLike): boolean {
  return getStringCandidates(event).some((candidate) =>
    METAMASK_ERROR_PATTERNS.some((pattern) => candidate.includes(pattern)),
  )
}

function isKnownExtensionFrame(frame: { filename?: unknown; abs_path?: unknown }): boolean {
  const filename = typeof frame.filename === 'string' ? frame.filename : ''
  const absPath = typeof frame.abs_path === 'string' ? frame.abs_path : ''
  return KNOWN_EXTENSION_FRAME_PATTERNS.some((pattern) => filename.includes(pattern) || absPath.includes(pattern))
}

function hasOnlyExtensionFrames(event: SentryEventLike): boolean {
  const values = event.exception?.values || []
  const allFrames = values.flatMap((v) => v.stacktrace?.frames || [])
  if (allFrames.length === 0) return false
  // Drop ONLY when every frame is from a known extension origin.
  // Any unrecognized frame (production bundle URL, source-mapped path, etc.)
  // is assumed first-party — err on the side of keeping the event.
  return allFrames.every((frame) => isKnownExtensionFrame(frame))
}

// Firefox (and occasionally Chrome) throws NetworkError from the browser's font-loading
// pipeline when a font request is cancelled or a CDN blip happens. Captured via
// console.error("Font loading failed:", err) — not actionable (NONRESIDENT-AQ).
const FONT_LOADING_PATTERN = 'Font loading failed'

function hasFontLoadingMessage(event: SentryEventLike): boolean {
  return getStringCandidates(event).some((candidate) => candidate.includes(FONT_LOADING_PATTERN))
}

export function shouldDropFontLoadingNetworkError(event: SentryEventLike): boolean {
  return hasFontLoadingMessage(event) && hasConsoleOrigin(event)
}

export function shouldDropMetaMaskExtensionError(event: SentryEventLike): boolean {
  // Message-only events (no stack frames) are safe to drop if the message
  // is MetaMask-specific — these strings never appear in our own error handling.
  // Frame-based: drop only when ALL frames are from known extension origins.
  if (hasOnlyExtensionFrames(event)) return true
  // For message-based detection without frames, check if any exception has frames
  // that are NOT extension frames — if so, our code is involved, keep the event.
  const values = event.exception?.values || []
  const allFrames = values.flatMap((v) => v.stacktrace?.frames || [])
  if (allFrames.length > 0 && !allFrames.every((frame) => isKnownExtensionFrame(frame))) {
    return false
  }
  return hasMetaMaskErrorMessage(event)
}

const FIDO2_EXTENSION_ERROR_PATTERNS = ['fido2', 'web authn', 'webauthn', 'security key', 'content script']

function hasFido2ExtensionMessage(event: SentryEventLike): boolean {
  return getStringCandidates(event).some((candidate) => {
    const normalized = candidate.toLowerCase()
    return FIDO2_EXTENSION_ERROR_PATTERNS.some((pattern) => normalized.includes(pattern))
  })
}

export function shouldDropFido2ExtensionOnlyError(event: SentryEventLike): boolean {
  return hasFido2ExtensionMessage(event) && hasOnlyExtensionFrames(event)
}

// A password-manager or autofill extension's content script logs this from the
// sign-in form; the string exists nowhere in our code or bundle, and
// captureConsoleIntegration forwards it (NONRESIDENTTAX-F2).
const EXTENSION_FOCUS_WARNING = 'could not ensure focus'

export function shouldDropExtensionFocusWarning(event: SentryEventLike): boolean {
  return (
    hasConsoleOrigin(event) &&
    getStringCandidates(event).some((candidate) => candidate.trim() === EXTENSION_FOCUS_WARNING)
  )
}

// `/dev/*` previews fake a signed-in session and their API calls fail by
// design; in a production build every `/dev/*` route is notFound(), so no
// customer reaches one. An event from such a page is a verification harness
// talking to Sentry (NONRESIDENTTAX-F3).
function eventPagePath(event: SentryEventLike): string | null {
  const url = typeof event.request?.url === 'string' ? event.request.url : event.tags?.url
  if (typeof url !== 'string') return null
  try {
    return new URL(url).pathname
  } catch {
    return null
  }
}

export function shouldDropDevPreviewEvent(event: SentryEventLike): boolean {
  const path = eventPagePath(event)
  return path !== null && (path === '/dev' || path.startsWith('/dev/'))
}
